
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Moment } from 'moment-timezone';
import { json2csv } from 'json-2-csv';
import fileDownload from 'js-file-download';
import OrderedQueue from '@/types/OrderedQueue';
import QueueEntryDisplay from '@/components/ordered/QueueEntry.vue';
import QueueSignup from '@/components/ordered/QueueSignup.vue';
import ErrorDialog from '@/util/ErrorDialog';

import { library } from '@fortawesome/fontawesome-svg-core';
import {
	faStoreAltSlash,
	faGrinHearts,
	faHeartBroken,
	faUserGraduate,
	faEraser,
	faCalendarAlt,
	faDownload,
	faDice,
	faLock,
	faLockOpen,
} from '@fortawesome/free-solid-svg-icons';
import OrderedSchedule from './OrderedSchedule.vue';

library.add(
	faStoreAltSlash,
	faGrinHearts,
	faHeartBroken,
	faUserGraduate,
	faEraser,
	faCalendarAlt,
	faDownload,
	faDice,
	faLock,
	faLockOpen
);

@Component({
	components: {
		QueueSignup,
		QueueEntryDisplay,
		OrderedSchedule,
	},
})
export default class OrderedQueueDisplay extends Vue {
	@Prop({ required: true }) queue!: OrderedQueue;
	@Prop({ required: true }) loaded!: boolean;
	@Prop({ required: true }) ws!: WebSocket;
	@Prop({ required: true }) admin!: boolean;
	@Prop({ required: true }) time!: Moment;

	get open() {
		return this.queue.config?.scheduled ? this.scheduledOpen : this.queue.open;
	}

	get scheduledOpen() {
		return this.queue.scheduledOpen(this.time);
	}

	get closesAt() {
		return this.queue
			.halfHourToTime(
				this.queue.getNextCloseTime(this.queue.getHalfHour(this.time))
			)
			.format('LT');
	}

	get opensAt() {
		if (!this.queue.config?.scheduled) {
			return (
				'will be ' +
				(this.queue.open ? 'closed' : 'opened') +
				' manually by staff'
			);
		}

		const halfHour = this.queue.getNextOpenHalfHour(
			this.queue.getHalfHour(this.time)
		);

		if (halfHour === -1) {
			return 'is closed for the day';
		}

		return `opens at ${this.queue.halfHourToTime(halfHour).format('LT')}`;
	}

	clearQueue() {
		this.$buefy.dialog.confirm({
			title: 'Clear Queue',
			message: `Are you sure you want to clear the queue? <b>There's no undo; please don't use this to pop individual students.</b>`,
			type: 'is-danger',
			hasIcon: true,
			onConfirm: () => {
				fetch(process.env.BASE_URL + `api/queues/${this.queue.id}/entries`, {
					method: 'DELETE',
				}).then((res) => {
					if (res.status !== 204) {
						return ErrorDialog(res);
					}
				});
			},
		});
	}

	randomizeQueue() {
		this.$buefy.dialog.confirm({
			title: 'Randomize Queue',
			message: `Are you sure you want to randomize the queue? This will place everybody currently on the queue in a random position. This does not take into account the first-of-the-day status if your course uses it; consider whether this is okay. <b>There's no undo.</b>`,
			type: 'is-danger',
			hasIcon: true,
			onConfirm: () => {
				fetch(
					process.env.BASE_URL +
						`api/queues/${this.queue.id}/entries/randomize`,
					{
						method: 'POST',
					}
				).then((res) => {
					if (res.status !== 204) {
						return ErrorDialog(res);
					}
				});
			},
		});
	}

	editSchedule() {
		fetch(process.env.BASE_URL + `api/queues/${this.queue.id}/schedule`)
			.then((res) => res.json())
			.then((schedule) => {
				this.$buefy.modal.open({
					parent: this,
					component: OrderedSchedule,
					props: { defaultSchedule: schedule },
					events: {
						confirmed: (schedule: string[]) => {
							fetch(
								process.env.BASE_URL + `api/queues/${this.queue.id}/schedule`,
								{
									method: 'PUT',
									body: JSON.stringify(schedule),
								}
							).then((res) => {
								if (res.status !== 204) {
									return ErrorDialog(res);
								}
							});
						},
					},
					hasModalCard: true,
					trapFocus: true,
				});
			});
	}

	broadcast() {
		this.$buefy.dialog.prompt({
			message: `Broadcast message to all online users of queue`,
			trapFocus: true,
			onConfirm: (message) => {
				fetch(process.env.BASE_URL + `api/queues/${this.queue.id}/messages`, {
					method: 'POST',
					body: JSON.stringify({
						receiver: '<broadcast>',
						content: message,
					}),
				}).then((res) => {
					if (res.status !== 201) {
						return ErrorDialog(res);
					}
				});
			},
		});
	}

	downloadStackAsCSV() {
		fetch(process.env.BASE_URL + `api/queues/${this.queue.id}/stack`)
			.then((res) => res.json())
			.then((stack) => {
				json2csv(stack, (err, csv) => {
					if (err !== null || csv === undefined) {
						this.$buefy.dialog.alert({
							title: 'Error',
							message: `Failed to create csv: ${err}`,
							type: 'is-danger',
							hasIcon: true,
						});
						return;
					}
					fileDownload(csv, 'stack.csv', 'text/csv');
				});
			});
	}

	setOpen(open: boolean) {
		fetch(
			process.env.BASE_URL +
				`api/queues/${this.queue.id}/configuration/manual-open?` +
				new URLSearchParams({ open: open.toString() }),
			{
				method: 'PUT',
			}
		).then((res) => {
			if (res.status !== 204) {
				return ErrorDialog(res);
			}
		});
	}
}
